import React, { useEffect, useState, useCallback } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { academyAPI } from '../../utils/api';
import UsageMeter from '../../components/student/UsageMeter';

const MasteryBar = ({ value }) => {
  const v = value || 0;
  const color = v >= 75 ? 'bg-green-500' : v >= 40 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${Math.min(100, v)}%` }} />
    </div>
  );
};

const ProgressView = () => {
  const [progress, setProgress] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, u] = await Promise.allSettled([academyAPI.getProgress(), academyAPI.getUsage()]);
    if (p.status === 'fulfilled') setProgress(p.value.data || []);
    if (u.status === 'fulfilled') setUsage(u.value.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
      <div className="mb-6 flex items-center gap-2">
        <ChartBarIcon className="h-6 w-6 text-primary-500" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Your Progress</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">Track mastery across the topics you have studied.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-medium text-gray-900 dark:text-white">Topic mastery</h2>
            </div>
            {progress.length === 0 ? (
              <p className="px-5 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                No progress recorded yet. Study a topic or take a quiz to get started.
              </p>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Topic</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/3">Mastery</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quizzes</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Best</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {progress.map((p) => (
                        <tr key={p.id}>
                          <td className="px-5 py-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{p.topic_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{p.area_name}</p>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <MasteryBar value={p.mastery} />
                              <span className="text-xs text-gray-600 dark:text-gray-300 w-9 text-right">{p.mastery ?? 0}%</span>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-300">{p.quizzes_taken ?? 0}</td>
                          <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-300">{p.best_score != null ? `${p.best_score}%` : '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <ul className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
                  {progress.map((p) => (
                    <li key={p.id} className="px-5 py-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{p.topic_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{p.area_name}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <MasteryBar value={p.mastery} />
                        <span className="text-xs text-gray-600 dark:text-gray-300 w-9 text-right">{p.mastery ?? 0}%</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {p.quizzes_taken ?? 0} quizzes · best {p.best_score != null ? `${p.best_score}%` : '–'}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <UsageMeter usage={usage} />
        </div>
      </div>
    </div>
  );
};

export default ProgressView;
